import { Box, Card, CardContent } from '@mui/material';
import { t } from 'i18next';
import { Severity } from '../../../../common/enums/Severity';
import { ReportExportOptions } from '../../../options/ReportExportOptions';
import { BlankState } from '../../common/components/BlankState';
import { HiddenIssues } from '../../common/components/HiddenIssues';
import { Inventory } from '../../common/components/Inventory';
import { ReportSummaryDetails } from '../../common/components/ReportHeader';
import { BasePrintController, BasePrintControllerProps, BasePrintControllerState } from '../../common/controllers/BasePrintController';
import { IssueGroup } from '../components/IssueGroup';
import { JavaProvidersTable } from '../components/JavaProvidersTable';
import { Overview } from '../components/Overview';
import { ReportCallLimiting } from '../components/ReportCallLimiting';
import { StatsTable } from '../components/StatsTable';
import { ReportPrintData } from '../models/ReportPrintData';
import { ReportPrintRepository } from '../services/ReportPrintRepository';

type Props = BasePrintControllerProps & {
  reportId: number;
  options: ReportExportOptions;
};

type State = BasePrintControllerState & {};

export class ReportPrintController extends BasePrintController<Props, State> {
  private readonly repository = new ReportPrintRepository();
  private report: ReportPrintData | null = null;

  componentDidMount(): void {
    this.fetchReport(this.props.reportId);
  }

  componentWillUnmount(): void {
    this.repository.cancelAllRequests();
  }

  render() {
    if (!this.report) return <></>;

    const hasPriorityIssues = this.report.hasPriorityIssues();
    const hasHiddenIssues = this.report.hasHiddenIssues();
    const issueGroups = this.report.getIssueGroups();
    const instanceCounters = this.report.getInstancesCounters();
    const stats = this.report.getStats();
    const inventory = this.report.getInventory();
    const callLimiting = this.report.getCallLimiting();
    const javaProviders = this.report.getJavaProviders();

    // TODO(k.corizi) `instanceCounters` is only `null` when Exports V1 are disabled. Remove
    // this check when the feature flag is deprecated
    const showIssuesCharts = (hasHiddenIssues || hasPriorityIssues) && instanceCounters !== null;

    const hasInventory = inventory !== null && !inventory.isEmpty();
    const hasJavaProviders = javaProviders.length > 0;
    const hasStats = stats.length > 0;

    const hasAnyContent = Boolean(hasInventory || hasJavaProviders || hasStats || hasPriorityIssues || hasHiddenIssues || callLimiting);

    return this.renderBase(
      <Box>
        <Box sx={{ mb: 2 }}>
          <ReportSummaryDetails report={this.report} />
        </Box>
        {hasAnyContent ? (
          <>
            {showIssuesCharts && (
              <Box sx={{ mb: 2 }}>
                <Overview issueGroups={issueGroups} instanceCounters={instanceCounters} />
              </Box>
            )}
            {hasInventory && (
              <Box sx={{ mb: 2 }}>
                <Inventory inventory={inventory} />
              </Box>
            )}
            {hasJavaProviders && (
              <Box sx={{ mb: 2 }}>
                <JavaProvidersTable javaProviders={javaProviders} />
              </Box>
            )}
            {hasStats && (
              <Box sx={{ mb: 2 }}>
                <StatsTable stats={stats} />
              </Box>
            )}
            {hasPriorityIssues && (
              <Card>
                <CardContent>
                  <IssueGroup title={t('criticality.high')} issues={issueGroups[Severity.HIGH]} />
                  <IssueGroup title={t('criticality.med')} issues={issueGroups[Severity.MED]} />
                  <IssueGroup title={t('criticality.low')} issues={issueGroups[Severity.LOW]} />
                  <IssueGroup title={t('criticality.passed')} issues={issueGroups[Severity.PASSED]} />
                  {hasHiddenIssues && <HiddenIssues />}
                </CardContent>
              </Card>
            )}
            {!hasPriorityIssues && hasHiddenIssues && <HiddenIssues section />}
            {callLimiting && (
              <Box sx={{ mt: 2 }}>
                <ReportCallLimiting limits={callLimiting} />
              </Box>
            )}
          </>
        ) : (
          <BlankState title={t('blankReportSummary.title')} subtitles={[t('blankReportSummary.subtitle1'), t('blankReportSummary.subtitle2')]} />
        )}
      </Box>,
      { footer: hasAnyContent }
    );
  }

  private async fetchReport(reportId: number) {
    try {
      this.report = await this.repository.getReport(reportId, this.props.options);
      this.onRendered();
    } catch (e: unknown) {
      this.onError(e);
    }
  }
}
